import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Building2, Users, Briefcase, Settings } from "lucide-react";
import {
  useCompanyUsersForPlatform,
  useCompanyProjectsForPlatform,
  type PlatformCompanySummary,
} from "@/hooks/usePlatformSummary";

const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD",
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(v);

interface Props {
  company: PlatformCompanySummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEditSubscription: () => void;
}

const CompanyDetailDrawer = ({ company, open, onOpenChange, onEditSubscription }: Props) => {
  const { data: users = [], isLoading: loadingUsers } = useCompanyUsersForPlatform(
    open ? company?.company_id ?? null : null,
  );
  const { data: projects = [], isLoading: loadingProjects } = useCompanyProjectsForPlatform(
    open ? company?.company_id ?? null : null,
  );

  if (!company) return null;

  const adminUser = users.find((u) => u.role_name === "super_admin");

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            {company.company_name}
          </SheetTitle>
          <SheetDescription>Tenant detail view</SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Subscription — PLATFORM CONTEXT ONLY */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-muted-foreground">Subscription</h3>
              <Button variant="outline" size="sm" onClick={onEditSubscription}>
                <Settings className="h-3.5 w-3.5 mr-1" /> Manage
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Plan</span>
                <p className="font-semibold capitalize">{company.plan_tier}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Status</span>
                <p className="font-semibold capitalize">{company.subscription_status.replace("_", " ")}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Calculated MRR</span>
                <p className="font-semibold">{fmt(company.calculated_mrr)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Base Rate</span>
                <p className="font-semibold">{fmt(company.monthly_rate)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Paid Seats</span>
                <p className="font-semibold">{company.paid_seat_count} / {company.included_seats} included</p>
              </div>
              <div>
                <span className="text-muted-foreground">Contractors</span>
                <p className="font-semibold">{company.contractor_count} / {company.included_contractors} included</p>
              </div>
            </div>
          </section>

          <Separator />

          {/* Admin Contact */}
          {adminUser && (
            <>
              <section>
                <h3 className="text-sm font-medium text-muted-foreground mb-2">Admin Contact</h3>
                <p className="text-sm font-medium">
                  {[adminUser.first_name, adminUser.last_name].filter(Boolean).join(" ") || "—"}
                </p>
                <p className="text-sm text-muted-foreground">{adminUser.email}</p>
              </section>
              <Separator />
            </>
          )}

          {/* Users */}
          <section>
            <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
              <Users className="h-4 w-4" /> Users ({users.length})
            </h3>
            {loadingUsers ? (
              <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
            ) : users.length === 0 ? (
              <p className="text-sm text-muted-foreground">No users found.</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {users.map((u) => (
                  <div key={u.user_id} className="flex items-center justify-between text-sm border rounded-md px-3 py-2">
                    <div>
                      <p className="font-medium">
                        {[u.first_name, u.last_name].filter(Boolean).join(" ") || u.email || "—"}
                      </p>
                      {u.email && <p className="text-xs text-muted-foreground">{u.email}</p>}
                    </div>
                    <Badge variant="secondary" className="text-xs capitalize">
                      {u.role_name?.replace("_", " ") || "no role"}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </section>

          <Separator />

          {/* Projects */}
          <section>
            <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
              <Briefcase className="h-4 w-4" /> Projects ({projects.length})
            </h3>
            {loadingProjects ? (
              <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
            ) : projects.length === 0 ? (
              <p className="text-sm text-muted-foreground">No projects found.</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {projects.map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-sm border rounded-md px-3 py-2">
                    <div>
                      <p className="font-medium">{p.name}</p>
                      {p.project_number && <p className="text-xs text-muted-foreground">#{p.project_number}</p>}
                    </div>
                    <Badge variant="outline" className="text-xs capitalize">{p.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CompanyDetailDrawer;
